import { useEffect } from "react";
import { useShedConfig } from "../../contexts/ShedConfigContext";
import InfoTooltip from "../layout/InfoTooltip";
import ShedStyleSelector from "./ShedStyleSelector";
import DimensionsForm from "./DimensionsForm";
import DoorsAccordion from "./DoorsAccordion";
import ExtrasAccordion from "./ExtrasAccordion";

export default function ConfigDrawer() {
  const { drawerOpen, closeDrawer, applyConfig } = useShedConfig();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && drawerOpen) {
        closeDrawer();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const handleApply = () => {
    applyConfig();
  };

  if (!drawerOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="slide-over-title"
    >
      <div
        className="fixed inset-0 bg-gray-500/75 transition-opacity"
        aria-hidden="true"
        onClick={closeDrawer}
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 md:pl-10">
            <div
              id="drawer-panel"
              className="pointer-events-auto relative w-screen md:w-auto md:max-w-xl transform transition-transform duration-300 ease-in-out"
            >
              <div className="absolute top-0 left-0 ml-0 md:ml-8 flex pt-4 pr-2 sm:ml-10 sm:pr-4 z-10">
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="relative rounded-md text-gray-300 hover:text-white focus:ring-2 focus:ring-white focus:outline-none bg-gray-800 md:bg-transparent p-2 md:p-0"
                >
                  <span className="sr-only">Close panel</span>
                  <svg
                    className="size-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18 18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex h-full flex-col overflow-y-scroll bg-[#fcf8efe1] py-6 shadow-xl">
                <div className="px-4 sm:px-6">
                  <h2
                    className="text-center text-2xl font-semibold text-gray-900"
                    id="slide-over-title"
                  >
                    Installation Configuration
                    <InfoTooltip>
                      Here you'll enter the technical details of your structure.
                      Make sure to complete each field with real or projected
                      measurements.
                    </InfoTooltip>
                  </h2>
                </div>

                <div className="relative mt-6 flex-1 px-4 sm:px-6">
                  <form className="mb-5 p-4 border border-gray-300 rounded bg-[#fcf8ef83]">
                    <ShedStyleSelector />

                    <hr className="my-6 border-t border-gray-300 w-11/12 mx-auto rounded" />

                    <DimensionsForm />

                    <hr className="my-6 border-t border-gray-300 w-11/12 mx-auto rounded" />

                    <DoorsAccordion />

                    <ExtrasAccordion />

                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleApply}
                        className="bg-[#2f5241] hover:bg-[#2f5241e1] text-white px-5 py-2 rounded mt-4 w-full md:w-auto hover:cursor-pointer"
                      >
                        Send to PDF{" "}
                        <InfoTooltip>
                          Once doors and extras are selected, click Apply
                          changes to save your configuration and reflect it in
                          the plan and final technical report.
                        </InfoTooltip>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
